import * as React from 'react'
import Immutable from 'seamless-immutable'
import { useSelector } from 'react-redux'
import { useTranslate } from 'react-translate'
import { toast } from 'react-toastify'
import { Button } from '@scuf/common'

import { selectedOrgId } from 'Selectors/LoaderSelector'
import { useDownloadImportTemplate, useImportForm } from './import-employees-hooks'
import { ValidationStatus } from './validation-status'
import { Form, FormButtons, FormContent, Row, TableContainer } from './add-employees-form.styles'
import { EmployeesImportTable } from './employees-import-table'
import { ErrorToast, SuccessToast } from 'Themes/ScufStyledComponents'
import { NewEmployee } from '../../apis'
import { FileUploader } from './file-uploader'
import {
  FileDropContainer,
  StyledLoader
} from '../../../../Components/CsvFileDrop/csv-file-drop.styles'
export interface AddEmployeesFormProps {
  onCancel: () => any
  onSuccess: () => any
}

export const AddEmployeesForm: React.FC<AddEmployeesFormProps> = ({
  onCancel,
  onSuccess
}) => {
  const t = useTranslate('AddEmployeesForm')
  const orgId = useSelector(selectedOrgId)
  const { downloadImportTemplate } = useDownloadImportTemplate()
  const {
    validationStatus,
    isValid,
    isSubmitting,
    setTableData,
    tableData,
    submitForm,
    isBulkProcessing
  } = useImportForm()

  const onSubmit = React.useCallback(async () => {
    try {
      await submitForm()
      toast(<SuccessToast message={t('Success')} />)
      onSuccess()
    } catch (e) {
      console.error(e)
      toast(<ErrorToast message={t('ErrorSubmit')} />)
    }
  }, [t, onSuccess, submitForm])

  const setTableDataWithToasts = React.useCallback(async (data: Immutable.ImmutableArray<NewEmployee>) => {
    try {
      await setTableData(data)
    } catch (e) {
      console.error(e)
      toast(<ErrorToast message={t('ErrorOccurredValidating')} />)
    }
  }, [t, setTableData])

  const revalidateForm = React.useCallback(() => {
    setTableDataWithToasts(tableData)
  }, [setTableDataWithToasts, tableData])

  const onFileLoaded = React.useCallback(async (data: NewEmployee[]) => {
    await setTableDataWithToasts(Immutable(data))
  }, [setTableDataWithToasts])

  const onResetClick = React.useCallback(async () => {
    await setTableDataWithToasts(Immutable([] as NewEmployee[]))
  }, [setTableDataWithToasts])

  if (isSubmitting || isBulkProcessing) {
    let height='30vh'
    return (
      <FileDropContainer>
        <StyledLoader minHeight={height} />
      </FileDropContainer>
    )
  }

  return (
    <Form>
      <FormContent>
        <Row>
          <b>{`${t('TableLabel')} - `}</b>
          <Button
            type='link'
            content={t('DownloadTemplate')} onClick={() => downloadImportTemplate()}
          />
        </Row>
        <TableContainer>
          {
            tableData.length > 0
              ? (
                <EmployeesImportTable
                  data={tableData}
                  onChange={data => setTableDataWithToasts(data)}
                  validationStatus={validationStatus}
                  submitting={isSubmitting}
                />
              ) : <FileUploader organizationId={orgId} onFileLoaded={onFileLoaded} />
          }
        </TableContainer>
      </FormContent>
      <FormButtons>
        <Row>
          <Button
            id='reset-button'
            onClick={onResetClick}
            content={t('Reset')}
            disabled={tableData.length === 0}
            type='secondary'
          />
          <Button
            id='cancel-button'
            content={t('Cancel')}
            onClick={onCancel}
            disabled={isSubmitting}
            type='secondary'
          />
          {validationStatus === ValidationStatus.Failed && (
            <Button
              id='submit-button'
              content={t('Validate')}
              onClick={() => revalidateForm()}
              type='primary'
            />
          )}
          <Button
            id='submit-button'
            content={t('Submit')}
            onClick={onSubmit}
            disabled={!(isValid && validationStatus === ValidationStatus.Validated && tableData.length !== 0)}
            type='primary'
          />
        </Row>
      </FormButtons>
    </Form>
  )
}
